import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { DateRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import DateRangeIcon from "@mui/icons-material/DateRange";

const DateRangePicker = ({
  selectionRange,
  setSelectionRange,
  forecast = false,
}) => {
  const theme = useTheme();
  const [showPicker, setShowPicker] = useState(false);
  const [pickerPos, setPickerPos] = useState({ top: 0, left: 0 });
  // Local draft — not committed to parent until both dates are chosen
  const [draftRange, setDraftRange] = useState(selectionRange);
  // Track whether user has picked the start date but not yet the end date
  const [isSelectingEnd, setIsSelectingEnd] = useState(false);
  const triggerRef = useRef(null);
  const pickerRef = useRef(null);
  const isMobile = useMediaQuery("(max-width:900px)");

  // Keep draft in sync when parent resets selectionRange externally
  useEffect(() => {
    setDraftRange(selectionRange);
  }, [selectionRange]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        pickerRef.current &&
        !pickerRef.current.contains(e.target) &&
        triggerRef.current &&
        !triggerRef.current.contains(e.target)
      ) {
        // Discard incomplete draft and close
        setDraftRange(selectionRange);
        setIsSelectingEnd(false);
        setShowPicker(false);
      }
    };

    if (!isMobile && showPicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showPicker, isMobile, selectionRange]);

  const togglePicker = () => {
    if (!showPicker && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const pickerHeight = 330;
      const pickerWidth = 330;
      let top = rect.bottom + window.scrollY;
      let left = rect.left + window.scrollX;

      if (top + pickerHeight > window.innerHeight + window.scrollY) {
        top = rect.top - pickerHeight + window.scrollY;
      }

      if (left + pickerWidth > window.innerWidth + window.scrollX) {
        left = window.innerWidth - pickerWidth;
      }

      setPickerPos({ top, left });
    }
    if (showPicker) {
      // Closing without completing — discard draft
      setDraftRange(selectionRange);
      setIsSelectingEnd(false);
    }
    setShowPicker(!showPicker);
  };

  const handleSelect = (ranges) => {
    const next = ranges.selection;
    if (!isSelectingEnd) {
      // First click — user picked a start date; keep start === end as preview
      setDraftRange(next);
      setIsSelectingEnd(true);
    } else {
      // Second click — range is complete; commit to parent and close
      setDraftRange(next);
      setSelectionRange(next);
      setIsSelectingEnd(false);
      setShowPicker(false);
    }
  };

  const handleMobileClose = () => {
    // Discard incomplete draft
    setDraftRange(selectionRange);
    setIsSelectingEnd(false);
    setShowPicker(false);
  };

  const formatDate = (date) => {
    const d = String(date.getDate()).padStart(2, "0");
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const y = date.getFullYear();
    return `${d}/${m}/${y}`;
  };

  console.log(theme.palette.mode);

  return (
    <>
      <div
        ref={triggerRef}
        style={{ position: "relative", alignSelf: "end" }}
        className="date-range-picker"
      >
        {isMobile && !forecast ? (
          <IconButton onClick={() => setShowPicker(true)}>
            <DateRangeIcon />
          </IconButton>
        ) : (
          <div
            className="date-range-text"
            onClick={togglePicker}
            style={{
              fontFamily: "Poppins",
              fontSize: 12,
              height: 40,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              cursor: "pointer",
              color: "var(--text-primary)",
            }}
          >
            {`${formatDate(selectionRange.startDate)} - ${formatDate(
              selectionRange.endDate,
            )}`}
          </div>
        )}
      </div>

      {/* Mobile Picker in Dialog */}
      {isMobile && showPicker && (
        <Dialog open={showPicker} onClose={handleMobileClose}>
          <DialogContent>
            <DateRange
              ranges={[draftRange]}
              onChange={handleSelect}
              editableDateInputs
              moveRangeOnFirstSelection={false}
              maxDate={new Date()}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleMobileClose}>Cancel</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Desktop Picker in Portal */}
      {!isMobile &&
        showPicker &&
        ReactDOM.createPortal(
          <>
            <style>
              {`
                .rdrDateDisplayWrapper {
                  background-color: var(--bg-paper) !important;
                  color: var(--text-primary) !important;
                }
                .rdrDateInput,
                .rdrDateDisplayItem {
                  background-color: var(--bg-paper) !important;
                  color: var(--text-primary) !important;
                  border-color: var(--border-color) !important;
                }
                .rdrCalendarWrapper {
                  background-color: var(--bg-paper) !important;
                  color: var(--text-primary) !important;
                }
                .rdrMonth {
                  background-color: var(--bg-paper) !important;
                }
                .rdrMonthAndYearWrapper {
                  background-color: var(--bg-paper) !important;
                }
                .rdrMonthName {
                  color: var(--text-primary) !important;
                }
                .rdrNextPrevButton {
                  background-color: var(--hover-bg) !important;
                }
                .rdrNextPrevButton:hover {
                  background-color: var(--border-color) !important;
                }
                .rdrDay {
                  color: var(--text-primary) !important;
                }
                .rdrDayNumber {
                  color: var(--text-primary) !important;
                }
                .rdrDayNumber span {
                  color: var(--text-primary) !important;
                }
                .rdrInRange {
                  background-color: var(--primary-main)33 !important;
                }
                .rdrStartEdge,
                .rdrEndEdge,
                .rdrSelected {
                  background-color: var(--primary-main) !important;
                  color: var(--text-primary) !important;
                }
                .rdrInputElement {
                  background-color: var(--bg-paper) !important;
                  color: var(--text-primary) !important;
                  border-color: var(--border-color) !important;
                }
                .rdrInputElementWrapper {
                  background-color: var(--bg-paper) !important;
                }
                .rdrDefinedRangesWrapper {
                  background-color: var(--bg-paper) !important;
                  border-color: var(--border-color) !important;
                }
                .rdrStaticRange,
                .rdrStaticRangeLabel {
                  color: var(--text-primary) !important;
                }
              `}
            </style>
            <div
              ref={pickerRef}
              data-theme={theme.palette.mode}
              style={{
                position: "absolute",
                top: pickerPos.top,
                left: pickerPos.left,
                zIndex: 9999,
                backgroundColor: "var(--bg-paper)",
                color: "var(--text-primary)",
                border: "1px solid var(--border-color)",
                boxShadow: "var(--hover-bg) 0px 4px 12px",
                borderRadius: 4,
              }}
            >
              <DateRange
                ranges={[draftRange]}
                onChange={handleSelect}
                editableDateInputs
                moveRangeOnFirstSelection={false}
                maxDate={new Date()}
              />
            </div>
          </>,
          document.body,
        )}
    </>
  );
};

export default DateRangePicker;
